import { productCatalog } from "@holocron/shared";
import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { ProductScreenshotShowcase } from "../components/common/ProductScreenshotShowcase";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const solutionStats = [
  { value: "01", label: "Governed AI", copy: "Start with workflow design, policy boundaries, and deployment readiness." },
  { value: "02", label: "Compliant cloud", copy: "Use repeatable baselines and guardrails instead of rebuilding from scratch." },
  { value: "03", label: "Evidence loop", copy: "Keep findings, remediation, and artifacts connected to the operating state." }
];

const trustedPartners = [
  {
    name: "DEFCON AI",
    href: "https://www.defconai.com/",
    logoSrc: "/assets/img/defcon-ai-logo.png"
  },
  {
    name: "Kapule LLC",
    href: "https://www.kapulellc.com/",
    logoSrc: "/assets/img/kapule-logo.png"
  },
  {
    name: "Blaise Works",
    href: "https://www.blaiseworks.com/",
    logoSrc: "/assets/img/blaise-works-logo.svg"
  }
];

const productIconMap = {
  "secure-ai": "brain",
  "rapid-factory": "shield",
  "kyber-docs": "document",
  odan: "omni",
  mawforge: "orbit"
} as const;

const fitSignals = [
  "DIB organizations preparing for defense-sector requirements",
  "Teams deploying AI into regulated or sensitive workflows",
  "Operators who need security evidence to stay current",
  "Executives who need a clear authority model for automation",
  "Engineering teams building beyond single-tool security programs"
];

export function SolutionsPage() {
  useDocumentMeta({
    canonicalPath: "/solutions",
    description:
      "Holocron solutions connect secure AI implementation, CMMC environments, evidence operations, ODAN, and Mawforge for regulated organizations.",
    title: "Solutions | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: contactHref, label: "Plan AI Implementation", variant: "primary" },
          { href: "#platform", label: "View Products", variant: "secondary" }
        ]}
        copy="A product-led path for organizations that need AI, infrastructure, remediation, evidence, and authority to work under one operating model."
        eyebrow="Solutions"
        title="Conscious Security in practice."
      >
        <div className="hero-side-panel hero-side-panel--signal">
          <p className="section-kicker">Primary path</p>
          <h2>Implement AI. Build the environment. Prove the work.</h2>
          <div className="solution-hero-visual" aria-hidden="true">
            <span />
            <span />
            <span />
            <i />
          </div>
          <div className="signal-meter" />
        </div>
      </PageHero>

      <section className="surface-dark solution-proof-section">
        <div className="section-shell">
          <div className="proof-metric-grid proof-metric-grid--three">
            {solutionStats.map((stat) => (
              <article key={stat.label} className="proof-metric proof-metric--compact">
                <strong>{stat.value}</strong>
                <h3>{stat.label}</h3>
                <p>{stat.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark trusted-partners-section" aria-labelledby="trusted-partners-title">
        <div className="section-shell trusted-partners-shell">
          <div>
            <p className="section-kicker">Trusted partners</p>
            <h2 id="trusted-partners-title" className="section-title">
              Built alongside organizations that understand mission-critical execution.
            </h2>
          </div>
          <div className="trusted-partner-grid" aria-label="Holocron trusted partners">
            {trustedPartners.map((partner) => (
              <SmartLink
                key={partner.name}
                aria-label={`Visit ${partner.name}`}
                className="trusted-partner-card"
                href={partner.href}
                rel="noreferrer"
                target="_blank"
              >
                {partner.logoSrc ? (
                  <img alt={`${partner.name} logo`} src={partner.logoSrc} />
                ) : (
                  <span>{partner.name}</span>
                )}
                <span className="trusted-partner-cta">Visit site</span>
              </SmartLink>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark platform-section" id="platform">
        <div className="section-shell">
          <div className="section-intro">
            <p className="section-kicker">Holocron products</p>
            <h2 className="section-title">Five ways to put authority into operation.</h2>
            <p className="section-copy">
              Each product can stand alone, but the strongest outcome comes when AI, environments, evidence, visibility,
              and engineering capacity are designed together.
            </p>
          </div>

          <div className="solution-product-list">
            {productCatalog.map((product, index) => (
              <article id={product.id} key={product.id} className="solution-product-row">
                <div className="solution-product-index">{String(index + 1).padStart(2, "0")}</div>
                <div className="icon-badge icon-badge--glass">
                  <Icon name={productIconMap[product.id]} />
                </div>
                <div className="solution-product-rail" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div>
                  <h3>{product.title}</h3>
                  <p>{product.summary}</p>
                  <ul>
                    {product.capabilities.map((capability) => (
                      <li key={capability}>{capability}</li>
                    ))}
                  </ul>
                </div>
                <SmartLink className="inline-link" href={product.route === "/solutions#secure-ai" ? contactHref : product.route}>
                  {product.route.startsWith("/solutions#") ? "Discuss this capability" : `Explore ${product.title}`}
                </SmartLink>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ProductScreenshotShowcase
        className="product-screenshot-section--solutions"
        copy="RapidFactory and KyberDocs are the product surfaces that turn environment delivery, findings, evidence, and remediation into visible work."
        eyebrow="Product proof"
        title="Show the operating surfaces behind the solution."
      />

      <section className="surface-purple-glow">
        <div className="section-shell industry-shell">
          <div>
            <p className="section-kicker">Best fit</p>
            <h2 className="section-title">Organizations moving faster than manual governance can handle.</h2>
          </div>
          <div className="fit-panel fit-panel--dark fit-panel--sharp">
            <ul className="bullet-list">
              {fitSignals.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="surface-dark cta-band">
        <div className="section-shell section-shell--compact cta-center">
          <p className="section-kicker">Start with one outcome</p>
          <h2 className="section-title section-title--center">
            Tell us which mission problem you need to solve first.
          </h2>
          <div className="hero-actions hero-actions--centered">
            <SmartLink className="button primary" href={contactHref}>
              Contact Holocron
            </SmartLink>
            <SmartLink className="button secondary" href="/control-plane">
              View Architecture
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}
